import {
  forwardRef,
  type HTMLAttributes,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DiceGameContext } from '@components/DiceGame/context.ts';
import cn from 'classnames';
import { AnimatePresence, type Variants } from 'framer-motion';

import { MotionPicture } from '@/components/ui/Picture/Picture';

import { Tile } from '../Tile/Tile';
import { TilesBg } from '../TilesBg/TilesBg';

import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';

interface DiceTileProps extends HTMLAttributes<HTMLDivElement> {
  isActive?: boolean;
}

const diceVariants: Variants = {
  idle: {
    scale: 1,
    x: 0,
  },
  growing: {
    scale: 1.2,
    x: 0,
  },
  duplicating: {
    scale: [1.2, 1.2, 1.4, 0.7],
    x: -10,
  },
};

const DiceTile = forwardRef<HTMLDivElement, DiceTileProps>((props, ref) => {
  const { setDiceFromRef, setFlyingAnimation, flyingAnimation } =
    useContext(DiceGameContext);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const [isScaled, setIsScaled] = useState(false);

  useEffect(() => {
    if (imageRef.current && props.isActive) {
      setDiceFromRef(imageRef);
    }
  }, [props.isActive]);

  useEffect(() => {
    if (props.isActive) {
      setIsScaled(true);
      timerRef.current = setTimeout(() => {
        setFlyingAnimation('dice');
      }, 400);
    }
  }, [props.isActive]);

  useEffect(() => {
    if (flyingAnimation !== 'dice') {
      setIsScaled(false);
    }
  }, [flyingAnimation]);

  useEffect(() => {
    return (): void => {
      clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Tile
      ref={ref}
      {...props}
      variant={props.isActive ? 'dark' : 'grey'}
      className={cn(props.className, 'flex items-center justify-center')}
    >
      <AnimatePresence>
        <MotionPicture
          ref={imageRef}
          className="relative z-[3] w-[72%] h-[72%]"
          transition={{
            layout: { duration: 0.6, ease: 'easeOut' },
            scale: { duration: 0.4 },
          }}
          animate={{
            scale: isScaled ? 1.2 : 1,
          }}
          img={dicePng}
          webp={diceWebp}
          variants={diceVariants}
          alt="cash"
        />
      </AnimatePresence>
      <span className="absolute z-[4] px-1 py-[0.125rem] left-1/2 -translate-x-1/2 bottom-[9%] font-bold text-sm leading-none rounded-[0.375rem] backdrop-blur-sm bg-black/20">
        +2
      </span>
      {props.isActive && <TilesBg variant="green-active" />}
    </Tile>
  );
});

export { DiceTile };
